import React, { useContext, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { WorkspaceContext } from "../contexts/WorkspaceContext";
import { AuthContext } from "../contexts/AuthContext";
import {
  getWorkspaceMembres,
  updateMembreRole,
  removeWorkspaceMembre,
  updateWorkspaceApi,
  deleteWorkspaceApi,
} from "../services/workspace-management";
import Navbar from "../components/Navbar";
import Chatbot from "../components/chat/Chatbot";
import { IoMenuOutline } from "react-icons/io5";
import WorkspaceHeader from "../components/workspacesettings/WorkspaceHeader";
import WorkspaceInvitation from "../components/workspacesettings/WorkspaceInvitation";
import WorkspaceMembers from "../components/workspacesettings/WorkspaceMembers";
import WorkspacePermissionsList from "../components/workspacesettings/WorkspacePermissionsList";
import WorkspacePermissionsEdit from "../components/workspacesettings/WorkspacePermissionsEdit";
import WorkspaceGeneralSettings from "../components/workspacesettings/WorkspaceGeneralSettings";
import WorkspaceAccountCard from "../components/workspacesettings/WorkspaceAccountCard";
import WorkspaceOffcanvas from "../components/workspacesettings/WorkspaceOffcanvas";

export default function WorkspaceSettingsPage() {
  const navigate = useNavigate();
  const { currentWorkspace, workspaces, leaveWorkspace, hasPermission, selectWorkspace } =
    useContext(WorkspaceContext);
  const { user } = useContext(AuthContext);

  const ws = workspaces.find((w) => w._id === currentWorkspace?._id) || currentWorkspace;

  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [activeView, setActiveView] = useState("invitation");
  const [membres, setMembres] = useState([]);
  const [loadingMembres, setLoadingMembres] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingMembre, setEditingMembre] = useState(null);
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const offcanvasRef = useRef(null);

  useEffect(() => {
    if (!showOffcanvas) return;
    const handleClickOutside = (e) => {
      if (offcanvasRef.current && !offcanvasRef.current.contains(e.target)) {
        setShowOffcanvas(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOffcanvas]);

  const loadMembres = async () => {
    if (!ws) return;
    setLoadingMembres(true);
    try {
      const data = await getWorkspaceMembres(ws._id);
      if (data.membres) setMembres(data.membres);
    } catch {
      console.error("Erreur chargement membres");
    } finally {
      setLoadingMembres(false);
    }
  };

  useEffect(() => {
    if (activeView === "members" || activeView === "permissions" || activeView === "permissionsList") {
      loadMembres();
    }
  }, [activeView]);

  if (!ws) {
    return (
      <>
        <Navbar />
        <div className="tyn-content tyn-auth tyn-auth-centered">
          <p className="text-center text-muted">Aucun espace sélectionné</p>
        </div>
      </>
    );
  }

  const isOwner = ws.role === "owner";
  const isAdmin = isOwner || ws.role === "admin";

  const openMembrePerms = (membre) => {
    setEditingMembre(membre);
    setSelectedPerms([...membre.permissions]);
    setActiveView("permissions");
    setShowOffcanvas(false);
  };

  const togglePerm = (perm) => {
    setSelectedPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleRoleChange = async (membre, newRole) => {
    try {
      const data = await updateMembreRole(ws._id, membre._id, { role: newRole });
      if (data.membre) {
        setMembres((prev) =>
          prev.map((m) => (m._id === membre._id ? data.membre : m))
        );
      }
    } catch {
      alert("Erreur lors de la mise à jour du rôle.");
    }
  };

  const handleRoleChangeEdit = (membre, newRole) => {
    setEditingMembre({ ...editingMembre, role: newRole });
  };

  const savePermissions = async () => {
    if (!editingMembre) return;
    setSaving(true);
    try {
      const data = await updateMembreRole(ws._id, editingMembre._id, {
        permissions: selectedPerms,
      });
      if (data.membre) {
        setMembres((prev) =>
          prev.map((m) => (m._id === editingMembre._id ? data.membre : m))
        );
      }
      setEditingMembre(null);
      setActiveView("members");
    } catch {
      alert("Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const cancelPermissions = () => {
    setEditingMembre(null);
    setActiveView("members");
  };

  const handleRemoveMembre = async (membre) => {
    if (!window.confirm(`Retirer ${membre.prenom} ${membre.nom} de l'espace ?`))
      return;
    try {
      const data = await removeWorkspaceMembre(ws._id, membre._id);
      if (data.status === 200) {
        setMembres((prev) => prev.filter((m) => m._id !== membre._id));
      }
    } catch {
      alert("Erreur lors du retrait.");
    }
  };

  const currentUserMembre = membres.find((m) => m.userId === user?.userId);

  const handleGeneralSave = async (formData) => {
    setSavingGeneral(true);
    try {
      const data = await updateWorkspaceApi(ws._id, formData);
      if (data.workspace) {
        selectWorkspace({ ...currentWorkspace, ...data.workspace });
        setShowOffcanvas(false);
      } else {
        alert(data.msg || "Erreur lors de l'enregistrement.");
      }
    } catch {
      alert("Erreur réseau.");
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    try {
      const data = await deleteWorkspaceApi(ws._id);
      if (data.status === 200) {
        navigate("/workspaces", { replace: true });
      } else {
        alert(data.msg || "Erreur lors de la suppression.");
      }
    } catch {
      alert("Erreur réseau.");
    }
  };

  return (
    <>
      <Navbar />
      <div className="tyn-content">
        <div className="container py-4">
          <div className="d-flex justify-content-start mb-3">
            <button
              onClick={() => setShowOffcanvas(true)}
              style={{
                padding: 0,
                border: "none",
                background: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IoMenuOutline style={{ fontSize: "2.5rem", cursor: "pointer", color: "var(--bs-primary, #0d6efd)" }} />
            </button>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <WorkspaceHeader ws={ws} />

              {activeView === "invitation" && (
                <WorkspaceInvitation ws={ws} hasPermission={hasPermission} />
              )}

              {activeView === "members" && (
                <WorkspaceMembers
                  membres={membres}
                  loadingMembres={loadingMembres}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  isAdmin={isAdmin}
                  currentUserMembre={currentUserMembre}
                  user={user}
                  onRoleChange={handleRoleChange}
                  onOpenPerms={openMembrePerms}
                  onRemove={handleRemoveMembre}
                />
              )}

              {activeView === "permissionsList" && (
                <WorkspacePermissionsList
                  membres={membres}
                  loadingMembres={loadingMembres}
                  currentUserMembre={currentUserMembre}
                  user={user}
                  onOpenPerms={openMembrePerms}
                />
              )}

              {activeView === "permissions" && editingMembre && (
                <WorkspacePermissionsEdit
                  editingMembre={editingMembre}
                  selectedPerms={selectedPerms}
                  saving={saving}
                  onTogglePerm={togglePerm}
                  onSave={savePermissions}
                  onCancel={cancelPermissions}
                  onRoleChange={handleRoleChangeEdit}
                />
              )}

              {activeView === "general" && isOwner && (
                <WorkspaceGeneralSettings
                  ws={ws}
                  onSave={handleGeneralSave}
                  saving={savingGeneral}
                />
              )}

              <WorkspaceAccountCard user={user} ws={ws} />
            </div>
          </div>
        </div>
      </div>

      <WorkspaceOffcanvas
        showOffcanvas={showOffcanvas}
        activeView={activeView}
        isOwner={isOwner}
        isAdmin={isAdmin}
        hasPermission={hasPermission}
        onSelectView={setActiveView}
        onClose={() => setShowOffcanvas(false)}
        onLeave={() => {
          leaveWorkspace();
          navigate("/workspaces", { replace: true });
        }}
        onDeleteWorkspace={handleDeleteWorkspace}
        offcanvasRef={offcanvasRef}
      />
      <Chatbot />
    </>
  );
}
